import { useEffect, useRef, useState } from 'react';
import { EmptyState } from '@/components/ui/empty-state';
import { useCanAdjustAssessment, useHealthReadinessQuery } from '@/lib/api/health-readiness';
import { DomainList, LIST_FRAME } from './rail';
import { DomainPane } from './domain-pane';
import { ManageDomains } from './manage-domains';
import { useHrNavigate } from './navigation';
import { openFindingsOf } from './overview/overview-model';

/**
 * The Domains tab (T-0242, drawn in T-0243 / E22-F02): the domain list on
 * the left, the selected domain on the right, one direction. `/domains`
 * opens the first domain; `/domains/<id>` that one (a stale id falls back
 * to the first, as E20's `?item=` did). Leads open Manage domains from the
 * list. Prototype `domainsTab()`.
 */
export interface DomainsTabProps {
  engagementId: string;
  domainId?: string;
}

export function DomainsTab({ engagementId, domainId }: DomainsTabProps) {
  const { data: view } = useHealthReadinessQuery(engagementId);
  const canManage = useCanAdjustAssessment();
  const go = useHrNavigate(engagementId);
  const [manage, setManage] = useState(false);

  // A new domain from the URL closes the manage pane.
  useEffect(() => setManage(false), [domainId]);

  // A row picked low on a long list opens its detail in view.
  const detailRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = detailRef.current;
    if (el && el.getBoundingClientRect().top < 72) el.scrollIntoView?.({ block: 'start' });
  }, [domainId, manage]);

  if (!view) return null;
  const domain = view.domains.find((d) => d.id === domainId) ?? view.domains[0] ?? null;

  return (
    <div className="grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)] lg:items-start">
      <aside className={LIST_FRAME}>
        <DomainList
          view={view}
          selected={domain?.id ?? null}
          onSelect={(id) => {
            setManage(false);
            go({ tab: 'domains', id });
          }}
          canManage={canManage}
          manageActive={manage}
          onManage={() => setManage(true)}
        />
      </aside>
      <section ref={detailRef} aria-label="Detail" className="min-w-0 scroll-mt-[72px]">
        {manage && canManage ? (
          <ManageDomains
            engagementId={engagementId}
            domains={view.domains}
            onDone={() => setManage(false)}
          />
        ) : domain ? (
          <DomainPane
            key={domain.id}
            engagementId={engagementId}
            domain={domain}
            criteria={view.criteria}
            thresholds={view.verdict.readiness?.thresholds ?? null}
            openFindings={openFindingsOf(view, domain.id)}
          />
        ) : (
          <EmptyState
            title="No domains yet"
            description="Add the domains this engagement is assessed on from Manage domains."
          />
        )}
      </section>
    </div>
  );
}
